/**
 * The receive engine (FEAT-040 wave 3) — arrivals become downloads.
 *
 * The send engine's discipline, pointed the other way: one engine across
 * tabs (Web Lock, skip-not-queue), concurrency 2 (every media GET costs the
 * server a whole-file decrypt and media routes have no rate limit — the cap
 * IS the throttle, same as the prefetcher's), the uploadPolicy retry ladder,
 * 401 pauses without burning an attempt (BUG-069), and one timer armed for
 * the soonest retry — the weak-but-online fix.
 *
 * Deliberately LESS than the send side: no token buckets (no server limit to
 * mirror), no progress signal (nothing subscribes — the store's one onChange
 * is enough), and no stuck-row UI. A row that fails five times PARKS
 * invisibly: no timer, retried only on external kicks (online rise,
 * foreground, reconcile) — a failed download's fallback is today's working
 * online path, so there is no decision to put in front of anyone.
 *
 * Every fetch carries `Range: bytes=0-`. That flips the ALREADY-DEPLOYED
 * service worker's isDirectMediaRequest predicate to NetworkOnly — a plain
 * blob fetch would ride the api NetworkFirst route and race its 10s timer
 * into a guaranteed cache miss. An sw.ts fix instead would wait for every
 * phone's next clean relaunch (a new SW installs but does not take over);
 * the header works the moment this code runs. The server answers 206 with
 * the full body, and `response.ok` is true for 206.
 */
import { API_BASE_URL } from '../../api/client';
import type { Message } from '../../types';
import {
  computeUploadTimeoutMs,
  isPermanentStatus,
  retryDelayMs,
  STUCK_AFTER_FAILURES,
} from '../uploadPolicy';
import {
  ReceiveBudgetError,
  ReceiveRowGoneError,
  receiveStore,
  type InboxMeta,
} from './receiveStore';

const CONCURRENCY = 2;

/** Size guesses for the download timeout when the envelope carries no
 * media_size_bytes (pre-FEAT-028-w7 rows, re-shared Memories). Generous for
 * video ON PURPOSE: an 8 MiB guess would time out a legitimate slow 40 MB
 * download five times over, burning partial data on every attempt. */
const SIZE_GUESS_VIDEO = 64 * 1024 * 1024;
const SIZE_GUESS_PHOTO = 8 * 1024 * 1024;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class ReceiveEngineService {
  private draining = false;
  private rerun = false;
  private needsAuth = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private listening = false;
  private passes = 0;

  init(): Promise<void> {
    this.listen();
    return receiveStore.whenReady();
  }

  whenReady(): Promise<void> {
    return this.init();
  }

  private listen(): void {
    if (this.listening || typeof window === 'undefined') return;
    this.listening = true;
    window.addEventListener('online', () => this.kick());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.kick();
    });
  }

  /**
   * A moment arrived on the WebSocket — remember it durably, then download.
   * Media snaps only: texts have nothing to fetch, and the store's enqueue
   * is idempotent so a reconcile racing an arrival costs nothing.
   */
  async onArrival(userId: number, message: Message): Promise<void> {
    if (message.message_type === 'text' || !message.media_url) return;
    const added = await receiveStore.enqueue(userId, message);
    if (added) this.kick();
  }

  /** Ask for a pass. Cheap and idempotent — a pass already running just gets
   * asked to run again when it finishes (the send engine's BUG-055 rule). */
  kick(): void {
    this.passes++;
    void this.drain().finally(() => {
      this.passes--;
    });
  }

  /** Tests only: resolves when nothing is in flight. */
  async idle(): Promise<void> {
    for (let i = 0; i < 2_000; i++) {
      if (this.passes === 0 && !this.draining) return;
      await delay(5);
    }
  }

  private async drain(): Promise<void> {
    await this.init();
    if (this.draining) {
      this.rerun = true;
      return;
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    this.draining = true;
    // Every pass starts optimistic: if the session came back, the first
    // success clears the pause by itself.
    this.needsAuth = false;
    try {
      await this.withLock(() => this.pump());
    } catch (err) {
      console.error('[Inbox] download pass failed:', err);
    } finally {
      this.draining = false;
      this.scheduleRetry();
      if (this.rerun) {
        this.rerun = false;
        this.passes++;
        void this.drain().finally(() => {
          this.passes--;
        });
      }
    }
  }

  /** Exactly one downloader across tabs — a second copy would double every
   * whole-file decrypt the server pays for. Skip, never queue. */
  private async withLock(run: () => Promise<void>): Promise<void> {
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    if (!locks?.request) {
      await run();
      return;
    }
    await locks.request(
      'momento-receive-engine',
      { ifAvailable: true },
      async (lock) => {
        if (!lock) return;
        await run();
      }
    );
  }

  private async pump(): Promise<void> {
    // One try per row per pass: a PARKED row (attempts >= 5, no timer) is
    // still picked up here — that is exactly what "retried on external
    // kicks" means — but without this set a row failing in-pass would spin.
    const touched = new Set<number>();
    const worker = async (): Promise<void> => {
      for (;;) {
        if (this.needsAuth) return;
        if (typeof navigator !== 'undefined' && !navigator.onLine) return;
        const job = receiveStore
          .pending()
          .find(
            (row) =>
              !touched.has(row.messageId) &&
              (row.nextAttemptAt ?? 0) <= Date.now()
          );
        if (!job) return;
        touched.add(job.messageId);
        await this.download(job);
      }
    };
    await Promise.all(
      Array.from({ length: CONCURRENCY }, () => worker())
    );
  }

  private async download(row: InboxMeta): Promise<void> {
    const sizeGuess =
      row.envelope.media_size_bytes ??
      (row.envelope.media_type === 'video' ? SIZE_GUESS_VIDEO : SIZE_GUESS_PHOTO);
    const controller = new AbortController();
    const timeout = setTimeout(
      () => controller.abort(),
      computeUploadTimeoutMs(sizeGuess)
    );
    try {
      const response = await fetch(
        `${API_BASE_URL}/messages/${row.messageId}/media`,
        {
          credentials: 'include',
          headers: { Range: 'bytes=0-' },
          signal: controller.signal,
        }
      );

      if (response.status === 401) {
        // Signed out, not failed — zero attempts burned; sign-in kicks us.
        this.needsAuth = true;
        return;
      }
      if (!response.ok) {
        if (isPermanentStatus(response.status)) {
          // 403 expired / 404 gone / refused: the moment is not ours to
          // fetch and never will be. Drop the row; that conversation falls
          // back to the online path.
          await receiveStore.shred(row.messageId);
          return;
        }
        await this.noteFailure(row);
        return;
      }
      if (!response.body) {
        await this.noteFailure(row);
        return;
      }

      const declared = Number(response.headers.get('content-length'));
      const mime =
        response.headers.get('content-type') ?? 'application/octet-stream';
      await receiveStore.saveStream(
        row.messageId,
        response.body,
        mime,
        Number.isFinite(declared) && declared > 0 ? declared : undefined
      );
    } catch (err) {
      if (err instanceof ReceiveRowGoneError) return; // shredded mid-download — done
      if (err instanceof ReceiveBudgetError) {
        // Could never fit. Permanent: the row would retry forever otherwise.
        await receiveStore.shred(row.messageId);
        return;
      }
      await this.noteFailure(row);
    } finally {
      clearTimeout(timeout);
    }
  }

  private async noteFailure(row: InboxMeta): Promise<void> {
    const attempts = row.attempts + 1;
    if (attempts >= STUCK_AFTER_FAILURES) {
      // Parked: due whenever the next external kick arrives, but no timer of
      // its own — see the header for why there is no stuck UI here.
      await receiveStore.patch(row.messageId, {
        attempts,
        nextAttemptAt: undefined,
      });
    } else {
      await receiveStore.patch(row.messageId, {
        attempts,
        nextAttemptAt: Date.now() + retryDelayMs(attempts),
      });
    }
  }

  /** One timer for the soonest ladder retry — the weak-but-online fix.
   * Parked rows arm nothing. */
  private scheduleRetry(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    if (this.needsAuth) return; // a timer would just 401 on a loop
    const waiting = receiveStore
      .pending()
      .filter(
        (row) =>
          row.attempts > 0 &&
          row.attempts < STUCK_AFTER_FAILURES &&
          row.nextAttemptAt !== undefined
      );
    if (waiting.length === 0) return;
    const soonest = Math.min(...waiting.map((row) => row.nextAttemptAt!));
    this.retryTimer = setTimeout(
      () => {
        this.retryTimer = null;
        this.kick();
      },
      Math.max(soonest - Date.now(), 1_000)
    );
  }
}

export const receiveEngine = new ReceiveEngineService();
